import { useEffect, useState, useSyncExternalStore } from 'react';
import { BrandTemplateListAPIQueryParams, LayoutEnum, brandTemplateList } from '@goldcast/api/content';
import { useQuery } from 'react-query';
import { IStringifyOptions } from 'qs';
import { SizeType } from '@goldcast/player-config/src/types/asset-types';
import { doesClipWorkOnAlignedStreams, isOriginalLayoutAvailable } from '@goldcast/player-config/src/utils/clipUtils';
import TemplatePreview from './TemplatePreview';
import { PreviewTemplate } from '../types';
import { mapBrandedTemplatesResponse } from './utils';
import TemplatesEmptyState from '../TemplatesSideBar/TemplatesEmptyState';
import TemplatesErrorState from '../TemplatesSideBar/TemplatesErrorState';
import { useActiveSpeakerMatchCondition } from '../../ClipPlayer/ClipCustomizer/useActiveSpeakerMatchCondition';
import Loader from '@/components/atoms/Loader';
import { applyTemplate, isLayoutsWithVisionDataEnabled, templateLoader } from '@/stores/clip';
import { classnames } from '@/libs/utils';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { isAudioContent } from '@/stores/core';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

export default function TemplatesList({
  limit = 10,
  size,
  tags,
  showAll = true,
  queryParams = {},
  isInsidePopover = false
}: {
  limit?: number;
  size?: SizeType;
  tags?: string;
  showAll?: boolean;
  queryParams?: Partial<BrandTemplateListAPIQueryParams>;
  isInsidePopover?: boolean;
  isFullRecordingEdit?: boolean;
}) {
  const { clipId, clipData } = useClipsContext();
  const [templates, setTemplates] = useState<PreviewTemplate[]>([]);
  const templateLoaderState = useSyncExternalStore(templateLoader.subscribe, templateLoader.getSnapshot);

  const getLayouts = (): LayoutEnum[] => {
    if (isAudioContent()) {
      return ['AUDIOGRAM'];
    }
    const layouts: LayoutEnum[] = ['AUDIOGRAM'];
    if (isOriginalLayoutAvailable(clipData)) {
      layouts.push('DEFAULT');
    }
    if (doesClipWorkOnAlignedStreams(clipData) || isLayoutsWithVisionDataEnabled(clipData)) {
      layouts.push('SPEAKER', 'GRID', 'FOCUS', 'PIP');
    }
    return layouts;
  };

  // The frontend now owns the branded-presets decision (Use_CL_Branded_Presets):
  // the backend serves branded vs legacy system presets based on this flag.
  const brandedPresetsAvailable = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Branded_Presets];

  const {
    isFetching: loading,
    refetch,
    isError
  } = useQuery({
    queryKey: [size, tags, queryParams, brandedPresetsAvailable],
    queryFn: () =>
      brandTemplateList({
        queryParams: {
          limit,
          size,
          tags,
          layout: getLayouts(),
          is_branded: brandedPresetsAvailable,
          ...queryParams
        },
        queryParamsStringifyOptions: {
          arrayFormat: 'repeat'
        } as IStringifyOptions
      }),
    onSuccess: data => {
      const results = mapBrandedTemplatesResponse(clipId, data.results || []);
      setTemplates(showAll ? results : results.slice(0, 4));
    }
  });

  function reloadTemplates(params?: { template: PreviewTemplate }) {
    refetch().then(() => {
      if (params?.template) {
        applyTemplate(clipId, params.template);
      }
    });
  }

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.ReloadTemplates, reloadTemplates);
    return () => {
      EventBus.off(CustomEvents.ReloadTemplates, eventListener);
    };
  }, []);

  const { isSpeakerIdentificationMandatory } = useActiveSpeakerMatchCondition();

  return loading ? (
    <div className="flex h-80 items-center justify-center">
      <Loader />
    </div>
  ) : isError ? (
    <TemplatesErrorState />
  ) : templates.length ? (
    <div
      className={classnames(
        'mt-0.5 grid grid-cols-2 gap-3',
        templateLoaderState ? 'pointer-events-none cursor-progress opacity-70' : ''
      )}
    >
      {templates.map((template, index) => {
        return (
          <TemplatePreview
            template={template}
            key={template.id! + index}
            predefinedTooltipPosition={isInsidePopover ? (index % 2 !== 0 ? 'left' : 'right') : undefined}
            isSpeakerIdentificationMandatory={isSpeakerIdentificationMandatory}
          />
        );
      })}
    </div>
  ) : (
    <TemplatesEmptyState size={size} tag={tags} />
  );
}
